/**
 * QUIPWA — sync status (observable)
 *
 * One small store the whole UI reads: the sync chip in the header, the
 * Settings → "Cuenta y sincronización" section, the dashboard stamp. The
 * SyncEngine is the only writer.
 */
import { create } from 'zustand'

export type SyncPhase = 'idle' | 'state' | 'outbox' | 'decks' | 'notes'

export type SyncStatus =
  /** Everything we know is in Drive. */
  | 'synced'
  /** A sync is running. */
  | 'syncing'
  /** Local changes waiting for the next sync (normal between syncs). */
  | 'pending'
  /** No network. Local work continues; it syncs when the network returns. */
  | 'offline'
  /** No usable Google session: the user must reconnect. */
  | 'needs_auth'
  /** Signed in, but Google did not grant the Drive permission: the user must grant it. */
  | 'needs_consent'
  /** Signed out on purpose: nothing syncs. */
  | 'signed_out'
  /** The last attempt failed for another reason; retried automatically. */
  | 'error'

export interface SyncState {
  status: SyncStatus
  phase: SyncPhase
  /** ms epoch of the last fully successful sync. */
  lastSyncedAt: number | null
  lastError: string | null
  /** Outbox ops waiting to be sent. */
  pendingOps: number
  /** Outbox ops that failed too often and stopped retrying. */
  parkedOps: number
  /** The token lacks drive.appdata; state syncs through the legacy file meanwhile. */
  appDataUnavailable: boolean
  set: (patch: Partial<Omit<SyncState, 'set'>>) => void
}

export const useSyncStatus = create<SyncState>((set) => ({
  status: 'synced',
  phase: 'idle',
  lastSyncedAt: null,
  lastError: null,
  pendingOps: 0,
  parkedOps: 0,
  appDataUnavailable: false,
  set: (patch) => set(patch),
}))
